"use client";

import { Button } from "@/components/atoms/Button";
import { BlogProgramCardsCarousel } from "./BlogProgramCardsCarousel";
import CourseCard from "@/components/molecules/CourseCard";
import MobileCourseCard from "@/components/molecules/MobileCourseCard";
import {
  // [RUDDERSTACK-OLD disabled 2026-07-20] widget_viewed → central widget_impression; see webapp/docs/analytics
  // FireWidgetViewedEvent,
  blogOrProgramCard,
  ctaClick,
} from "@/customAnalytics/trackingEvents";
import { useIsMobile } from "@/hooks/useIsMobile";
import { cn } from "@/lib/utils";
import { ProgramCardMetadata } from "@/types/programCard";
import { PAGE_TYPES } from "../../../constants";
import Image from "next/image";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

/** Inactive cards must not render (handles 0 / "0" / false; avoids `Boolean("0") === true`). */
function isProgramCardRowActive(item: any): boolean {
  if (!item) return false;
  const ms = item.meta_status;
  const st = item.status;
  const explicitlyOff = (v: unknown) => v === false || v === 0 || v === "0";
  const explicitlyOn = (v: unknown) => v === true || v === 1 || v === "1";
  if (explicitlyOff(ms)) return false;
  if (explicitlyOn(ms)) return true;
  if (explicitlyOff(st)) return false;
  if (explicitlyOn(st)) return true;
  return ms === undefined && st === undefined;
}

/** Preview may set `status`; live API uses `meta_status` — `meta_status` off always wins. */
const isCourseMetaVisible = (item: any) => isProgramCardRowActive(item);

export interface ExploreCoursesProps {
  data: {
    title: string;
    title_text_color: string;
    /** Section-level background (responsive). Empty/undefined → no background applied. */
    desktop_background_color?: string;
    mobile_background_color?: string;
    card_background_color: string;
    load_more_text: string;
    load_more_icon?: string; // Deprecated, kept for backwards compatibility
    load_more_desktop_icon?: string;
    load_more_desktop_icon_alt?: string;
    load_more_mobile_icon?: string;
    load_more_mobile_icon_alt?: string;
    load_more_text_color?: string; // Deprecated, kept for backwards compatibility
    load_more_background_color?: string; // Deprecated, kept for backwards compatibility
    load_more_desktop_text_color?: string;
    load_more_mobile_text_color?: string;
    load_more_desktop_background_color?: string;
    load_more_mobile_background_color?: string;
    load_more_button_class_type?: string;
    load_more_arrow_variant?: string;
    metadata: ProgramCardMetadata[];
  };
  onBrochureDownload?: (courseId: string) => void;
  onKnowMore?: (courseId: string) => void;
  onCardClick?: (courseId: string) => void;
  className?: string;
  /** Blog Program Cards (variant 5): carousel when more than one card. */
  blogCarousel?: boolean;
  /** Narrow blog content column: left-aligned title, full-width rail. */
  blogRail?: boolean;
  /** When page is a blog article, show every visible card (no 6-at-a-time + Load more cap). */
  pageCategory?: string;
}

const ExploreCourses1: React.FC<ExploreCoursesProps> = ({
  data,
  onBrochureDownload,
  onKnowMore,
  onCardClick,
  className,
  blogCarousel = false,
  blogRail = false,
  pageCategory,
}) => {
  const DESKTOP_INITIAL = 6;
  const MOBILE_INITIAL = 3;
  const [displayCount, setDisplayCount] = useState(DESKTOP_INITIAL);
  const [effectApplied, setEffectApplied] = useState(false);
  const [columnsPerRow, setColumnsPerRow] = useState(3);
  const isMobile = useIsMobile(768);

  // Sync initial count when switching between mobile/desktop (only while still at initial state)
  useEffect(() => {
    if (isMobile === null) return;
    const target = isMobile ? MOBILE_INITIAL : DESKTOP_INITIAL;
    setDisplayCount((prev) =>
      prev === DESKTOP_INITIAL || prev === MOBILE_INITIAL ? target : prev,
    );
  }, [isMobile]);
  const ref = useRef<HTMLElement>(null);

  // Handle double nesting from PreviewRenderer (same pattern as Accreditation1)
  const sectionData = (data as any)?.data || data;

  const activeMetadata = useMemo(
    () =>
      sectionData?.metadata
        ?.filter(isCourseMetaVisible)
        .sort(
          (a: any, b: any) => (a.meta_priority ?? 0) - (b.meta_priority ?? 0),
        ) || [],
    [sectionData?.metadata],
  );

  const useBlogCarousel = Boolean(blogCarousel) && activeMetadata.length > 1;

  const isBlogPage =
    String(pageCategory ?? "").toLowerCase() === PAGE_TYPES.BLOG.toLowerCase();

  /** Variant 1 (and similar) on blog pages: avoid hiding cards behind Load more when not using the blog carousel. */
  const showAllCardsOnBlog = isBlogPage && !useBlogCarousel;

  // Track widget viewed
  // [RUDDERSTACK-OLD disabled 2026-07-20] impression now central; see webapp/docs/analytics
  // useEffect(() => {
  //   if (ref.current) {
  //     FireWidgetViewedEvent(ref, "explore_courses_section");
  //   }
  // }, []);

  useEffect(() => {
    const handleResize = () => {
      if (typeof window !== "undefined") {
        const width = window.innerWidth;
        if (width < 640) {
          setColumnsPerRow(2);
        } else {
          setColumnsPerRow(3);
        }
      }
    };

    handleResize(); // Set initial value
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Determine if a course is in the second row based on grid layout
  const isSecondRow = (index: number) => {
    if (isMobile) return false;
    const cols = blogRail ? 2 : columnsPerRow;
    return index >= cols && index < cols * 2;
  };

  const getBlurClassName = (index: number) => {
    if (!effectApplied || !isSecondRow(index)) {
      return ""; // No blur effect
    }

    return "scale-[0.95] opacity-40 pointer-events-none"; // Tailwind classes for the blur effect with no pointer events
  };

  const handleLoadMore = useCallback(() => {
    setEffectApplied(false);
    setDisplayCount(activeMetadata.length);

    ctaClick({
      section: "explore_courses_section",
      component: "load_more",
      label: sectionData?.load_more_text,
      step: "",
      entity: "load_more_button",
    });
  }, [activeMetadata, sectionData?.load_more_text]);

  // Wrapper for card click tracking
  const handleCardClickWithTracking = useCallback(
    (item: ProgramCardMetadata, index: number) => {
      // Track card click
      const url = item.meta_slug ? `/${item.meta_slug}` : item.meta_url || "";
      blogOrProgramCard({
        section: "explore_courses_section",
        component: "program_card",
        label: item.meta_subtext || item.id || "program_card",
        position: index + 1,
        url: url,
      });

      // Call original callback
      onCardClick?.(item.id);
    },
    [onCardClick],
  );

  const totalCount = activeMetadata.length;
  const hasMoreToLoad =
    !useBlogCarousel &&
    !showAllCardsOnBlog &&
    totalCount > 0 &&
    displayCount < totalCount;
  const showLoadMoreButton = hasMoreToLoad;

  // Apply effect when load more button is visible (only on mount)
  useEffect(() => {
    if (showLoadMoreButton && !effectApplied) {
      setEffectApplied(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array to only run once on mount

  if (!sectionData || !sectionData?.status) return null;

  const displayedCourses = useBlogCarousel
    ? activeMetadata
    : showAllCardsOnBlog
      ? activeMetadata
      : activeMetadata.slice(0, displayCount);

  // Section-level background (responsive). isMobile is null until mounted → desktop
  // value is used for SSR/first paint (matches card rendering). Only apply an inline
  // background when a colour is actually set, so existing sections (null columns)
  // keep their current transparent background unchanged.
  const sectionBackgroundColor =
    (isMobile
      ? sectionData?.mobile_background_color
      : sectionData?.desktop_background_color) || "";

  return (
    <section
      ref={ref}
      style={
        sectionBackgroundColor
          ? { backgroundColor: sectionBackgroundColor }
          : undefined
      }
      className={cn(
        "flex flex-col gap-6",
        blogRail
          ? /* Blog Program Cards column: left stack, no section padding from sm+ */
            "items-stretch justify-start px-1 py-6 sm:px-0 sm:py-1 sm:gap-6"
          : /* Default Program Cards / Explore layout: section is full-bleed so the
               background spans the viewport; inner grid stays centered/bounded. */
            "w-full items-center justify-center px-spacing-8 py-8 sm:px-9 sm:py-11 sm:gap-6",
        className,
      )}
    >
      <h2
        className={cn(
          blogRail
            ? "w-full self-start text-left"
            : "text-center max-w-[320px]",
          useBlogCarousel && blogRail
            ? "font-bold text-[24px] leading-snug sm:text-[26px] md:text-[28px] lg:text-[30px] xl:text-[32px]"
            : cn(
                "text-[26px] font-semibold",
                "md:text-[28px]",
                "lg:text-[34px]",
                "xl:text-[42px]",
              ),
          !blogRail && "sm:max-w-[440px]",
          !blogRail && "md:max-w-[510px]",
          !blogRail && "lg:max-w-[740px]",
          !blogRail && "xl:max-w-[740px]",
          "line-clamp-2 sm:line-clamp-1",
          "type-h3",
        )}
        style={{ color: sectionData?.title_text_color }}
      >
        {sectionData?.title}
      </h2>

      {useBlogCarousel ? (
        /** Full-bleed only below `sm` so desktop stays within the blog article column. */
        <div
          className={cn(
            "w-full shrink-0",
            "max-sm:w-screen max-sm:max-w-[100vw] max-sm:mx-[calc(50%-50vw)] max-sm:px-3",
          )}
        >
          <BlogProgramCardsCarousel
            items={activeMetadata}
            className="min-w-0"
            renderSlide={(item: ProgramCardMetadata, index: number) => (
              <CourseCard
                data={item}
                railLayout={blogRail}
                card_background_color={sectionData?.card_background_color}
                onBrochureDownload={() => onBrochureDownload?.(item?.id)}
                onKnowMore={() => onKnowMore?.(item?.id)}
                onCardClick={() => handleCardClickWithTracking(item, index)}
                blurClassName=""
                className={cn(
                  "box-border w-full min-w-0 max-w-full border-[#E0E0E0] shadow-[0_2px_16px_rgba(0,0,0,0.06)]",
                  // Slides fill their track, so every fixed card width has to be undone.
                  "sm:w-full sm:max-w-full md:w-full md:max-w-full lg:w-full lg:max-w-full xl:w-full xl:max-w-full",
                )}
              />
            )}
          />
        </div>
      ) : isMobile ? (
        // Mobile: marketing pages use stacked mobile cards; blog rail matches Atlas Figma (compact course card).
        <div className="flex w-full flex-col items-center gap-6">
          {displayedCourses.length > 0 &&
            displayedCourses?.map((item: ProgramCardMetadata, index: number) =>
              blogRail ? (
                <CourseCard
                  key={item?.id}
                  data={item}
                  railLayout
                  card_background_color={sectionData?.card_background_color}
                  onBrochureDownload={() => onBrochureDownload?.(item?.id)}
                  onKnowMore={() => onKnowMore?.(item?.id)}
                  onCardClick={() => handleCardClickWithTracking(item, index)}
                  blurClassName=""
                  className={cn(
                    "box-border w-full min-w-0 max-w-full border-[#E0E0E0] shadow-[0_2px_16px_rgba(0,0,0,0.06)]",
                  )}
                />
              ) : (
                <MobileCourseCard
                  key={item?.id}
                  data={item}
                  card_background_color={sectionData?.card_background_color}
                  onBrochureDownload={() => onBrochureDownload?.(item?.id)}
                  onKnowMore={() => onKnowMore?.(item?.id)}
                  onCardClick={() => handleCardClickWithTracking(item, index)}
                />
              ),
            )}
        </div>
      ) : (
        <div
          className={cn(
            "grid gap-x-6 gap-y-10 md:gap-10",
            // Section is full-bleed; the grid stays natural-width (fixed-width cards)
            // and centered so content keeps the bounds it was previously wrapped in.
            blogRail
              ? // Blog rail cards are `w-full`, so they need fractional columns to
                // size against until md, where the column list becomes intrinsic.
                "w-full grid-cols-2 md:w-fit md:mx-auto md:max-w-full md:grid-cols-[repeat(2,max-content)]"
              : // Marketing cards are fixed-width at every breakpoint, so the grid can
                // hug them and centre from the smallest non-mobile width up. With
                // `w-full` + fractional columns the cards hug the left of each column.
                "w-fit mx-auto max-w-full grid-cols-[repeat(2,max-content)] sm:grid-cols-[repeat(3,max-content)]",
          )}
        >
          {displayedCourses.length > 0 &&
            displayedCourses?.map(
              (item: ProgramCardMetadata, index: number) => (
                <CourseCard
                  key={item?.id}
                  data={item}
                  railLayout={blogRail}
                  card_background_color={sectionData?.card_background_color}
                  onBrochureDownload={() => onBrochureDownload?.(item?.id)}
                  onKnowMore={() => onKnowMore?.(item?.id)}
                  onCardClick={() => handleCardClickWithTracking(item, index)}
                  blurClassName={getBlurClassName(index)}
                />
              ),
            )}
        </div>
      )}

      {/* Load More Button */}
      {showLoadMoreButton && isMobile ? (
        <Button
          classType={
            (sectionData?.load_more_button_class_type as any) || "default"
          }
          withArrow={
            !!(
              sectionData?.load_more_mobile_icon || sectionData?.load_more_icon
            )
          }
          arrowIcon={
            sectionData?.load_more_mobile_icon ||
            sectionData?.load_more_icon ? (
              <Image
                src={
                  sectionData?.load_more_mobile_icon ||
                  sectionData?.load_more_icon ||
                  ""
                }
                alt={sectionData?.load_more_mobile_icon_alt || "load more"}
                width={20}
                height={20}
              />
            ) : undefined
          }
          arrowVariant={
            (sectionData?.load_more_arrow_variant as any) || "secondary"
          }
          arrowFill="white"
          onClick={handleLoadMore}
          className={cn("w-fit rounded-sm ", "xs:px-[10px] xs:py-[6px]")}
          style={{
            borderColor:
              sectionData?.load_more_mobile_background_color ||
              sectionData?.load_more_background_color,
            backgroundColor:
              sectionData?.load_more_mobile_background_color ||
              sectionData?.load_more_background_color,
          }}
        >
          <span
            className="text-btn-primary-1-semibold type-h6"
            style={{
              color:
                sectionData?.load_more_mobile_text_color ||
                sectionData?.load_more_text_color,
            }}
          >
            {sectionData?.load_more_text || "Load more"}
          </span>
        </Button>
      ) : (
        showLoadMoreButton && (
          <Button
            classType={
              (sectionData?.load_more_button_class_type as any) || "ghost"
            }
            withArrow={
              !!(
                sectionData?.load_more_desktop_icon ||
                sectionData?.load_more_icon
              )
            }
            arrowIcon={
              sectionData?.load_more_desktop_icon ||
              sectionData?.load_more_icon ? (
                <Image
                  src={
                    sectionData?.load_more_desktop_icon ||
                    sectionData?.load_more_icon ||
                    ""
                  }
                  alt={sectionData?.load_more_desktop_icon_alt || "load more"}
                  width={20}
                  height={20}
                />
              ) : undefined
            }
            arrowVariant={
              (sectionData?.load_more_arrow_variant as any) || "tertiary"
            }
            onClick={handleLoadMore}
          >
            <span
              className={cn(
                "text-heading-8-semibold",
                "md:text-heading-7-semibold",
                "lg:text-heading-6-semibold",
                "xl:text-heading-5-semibold",
                "type-h6",
              )}
              style={{
                color:
                  sectionData?.load_more_desktop_text_color ||
                  sectionData?.load_more_text_color,
              }}
            >
              {sectionData?.load_more_text || "Load more"}
            </span>
          </Button>
        )
      )}
    </section>
  );
};

export default ExploreCourses1;
